<template>
    <div class="editor__toolbar toolbar">
        <div class="toolbar__inner">
            <transition name="fade">
                <Spinner v-if="(saving || bookpart.in_queue) && !showAudioEditor">
                    {{ $t('editor.toolbar.generating') }}
                </Spinner>
            </transition>
            <transition name="fade" appear>
                <div v-if="available" class="toolbar__container">
                    <ErrorList :error="formattedError" />
                    <div class="toolbar__group">
                        <EditorToolbarPlayer
                            class="toolbar__player"
                            :audio="bookpart.audio"
                            @play="signalPlay"
                        />
                        <div class="toolbar__source-text" :class="{ rtl: isRtl }">
                            {{ bookpart.raw_text }}
                            <div class="toolbar__source-text-upper">
                                <button
                                    type="button"
                                    class="toolbar__help-button"
                                    @click="copyLink"
                                >
                                    {{ $t('editor.toolbar.copyLink') }}
                                </button>
                                <button
                                    type="button"
                                    class="toolbar__help-button"
                                    @click="copyText"
                                >
                                    {{ $t('editor.toolbar.copyText') }}
                                </button>
                            </div>
                        </div>
                        <template v-if="!readMode">
                            <div v-if="!isReviewer" class="toolbar__ssml">
                                <SsmlEditor
                                    :data="bookpart"
                                    :speaker-settings="speakerSettings"
                                    :speaker="speakerObject"
                                    :active="ssmlEditorActive"
                                    @massChange="onMassChange"
                                    @opened="ssmlEditorActive = true"
                                    @closed="ssmlEditorActive = false"
                                    @ssmlChanged="updateSsmlText"
                                />
                            </div>
                            <div v-if="hasChunkChanges" class="toolbar__warning">
                                You have Cut & Speed settings applied! Some of SSML settings might
                                not work properly!
                                <button type="button" class="link" @click="showAudioEditor = true">
                                    Cut & Speed Edit
                                </button>
                            </div>
                        </template>
                    </div>
                    <form class="toolbar__form form" @submit.prevent>
                        <portal-target name="toolbar" multiple />
                        <template v-if="!ssmlEditorActive">
                            <div class="toolbar__group">
                                <div class="toolbar__2halfs">
                                    <h4 v-if="!isReviewer" class="toolbar__heading">
                                        {{ $t('editor.toolbar.commonSettings') }}
                                    </h4>
                                </div>
                                <template v-if="!readMode && !isReviewer">
                                    <div class="toolbar__controls">
                                        <div class="editor-form__row">
                                            <NumberInput
                                                v-if="
                                                    speakerSettings.speed &&
                                                    speakerSettings.speed_type === 'input'
                                                "
                                                v-model="bookpartSpeed"
                                                :label="$t('misc.speed')"
                                                step="0.05"
                                                :min="0.5"
                                            />
                                            <NumberInput
                                                v-if="
                                                    speakerSettings.volume &&
                                                    speakerSettings.volume_type === 'input'
                                                "
                                                v-model="bookpartVolume"
                                                :label="$t('misc.volume')"
                                                step="0.05"
                                                :max="2"
                                                :min="0"
                                            />
                                        </div>
                                        <div class="editor-form__row">
                                            <button
                                                class="link"
                                                type="button"
                                                @click="showAudioEditor = true"
                                            >
                                                Cut & Edit Speed
                                            </button>
                                        </div>
                                        <div class="editor-form__row">
                                            <label class="editor-form__label">
                                                {{ $t('misc.language') }}
                                            </label>
                                            <CustomSelect
                                                v-model="language"
                                                emit-prop="id"
                                                with-filter
                                                view-label="label_value"
                                                :options="languages"
                                                :form-value-text="
                                                    (item) => item.label[$i18n.locale]
                                                "
                                            >
                                                <template #default="{ item }">
                                                    {{ item.label[$i18n.locale] }}
                                                </template>
                                            </CustomSelect>
                                        </div>
                                        <div class="editor-form__row">
                                            <label class="editor-form__label">
                                                {{ $t('misc.voice') }}
                                            </label>
                                            <SpeakerSelect
                                                v-model="bookpartSpeaker"
                                                :speakers="speakers"
                                                :placeholder="$t('editor.toolbar.pickVoice')"
                                                :empty-text="$t('editor.toolbar.noVoices')"
                                            />
                                        </div>
                                    </div>
                                    <!-- <div
                                        v-if="filteredSoundSettings.length"
                                        class="editor-form__row"
                                    >
                                        <label class="editor-form__label">
                                            {{ $t('editor.postproduction') }}
                                        </label>
                                        <CustomSelect
                                            v-model="bookpartSoundSettings"
                                            :options="filteredSoundSettings"
                                            :form-value-text="
                                                item => item.sound_setting_type.label[$i18n.locale]
                                            "
                                            emit-prop="id"
                                        >
                                            <template #default="{ item }">
                                                {{ item.sound_setting_type.label[$i18n.locale] }}
                                            </template>
                                        </CustomSelect>
                                    </div> -->
                                    <PaginatedResource
                                        v-if="speakerObject.id"
                                        ref="emotionsRes"
                                        :fetch-method="fetchEmotions"
                                        :filter-fields="emotionsFilter"
                                    >
                                        <template #default="{ loading, resource: emotions }">
                                            <div v-show="emotions.length" class="editor-form__row">
                                                <label class="editor-form__label">
                                                    {{ $t('editor.toolbar.emotion') }}
                                                </label>
                                                <CustomSelect
                                                    v-model="bookpartEmotion"
                                                    :form-value-text="({ name }) => name"
                                                    emit-prop="name"
                                                    :options="emotions"
                                                    :disabled="loading"
                                                    paginate
                                                    clearable
                                                    :paginate-amount="10"
                                                    with-filter
                                                    :popper-options="{ placement: 'top' }"
                                                >
                                                    <template #default="{ item }">
                                                        {{ item.name }}
                                                    </template>
                                                </CustomSelect>
                                            </div>
                                        </template>
                                    </PaginatedResource>
                                </template>
                            </div>
                        </template>
                        <div class="editor-form__row" style="text-align: right">
                            <button
                                v-if="$isManageApp"
                                style="margin-top: 1em"
                                class="button button--medium"
                                @click.prevent="save"
                            >
                                Generate
                            </button>
                            <NotesToolbar
                                v-if="!isEmbed && !readMode && bookpart.id"
                                style="margin-left: auto"
                                :book="book"
                                :bookpart="bookpart"
                            />
                        </div>
                    </form>
                </div>
            </transition>
            <transition name="fade">
                <div
                    v-if="!readMode && available && !isReviewer && changed"
                    class="toolbar__buttons"
                >
                    <button
                        type="button"
                        class="button button--negative toolbar__button"
                        @click.prevent="cancel"
                    >
                        {{ $t('editor.toolbar.cancel') }}
                    </button>
                    <button
                        type="button"
                        class="button toolbar__button"
                        :disabled="saving || !hasSpeaker"
                        @click.prevent="save"
                    >
                        {{ $t('editor.toolbar.save') }}
                    </button>
                </div>
            </transition>
        </div>
        <portal v-if="!readMode && bookpart && bookpart.audio && showAudioEditor" to="modals">
            <Modal
                name="audio-editor"
                :title="$t('audioEditor.title')"
                modal-class="audio-editor__modal"
                no-max-width
                @closed="showAudioEditor = false"
            >
                <!-- TODO -->
                <template v-if="bookpart.audio" #content>
                    <AudioEditor
                        ref="audioEditor"
                        :bookpart="bookpart"
                        @save="updateAudioEditorChunks"
                    />
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { NumberInput, CustomSelect, ErrorList } from '@speechki-frontend/shared/ui/forms';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import SpeakerSelect from '@speechki-frontend/shared/ui/SpeakerSelect';
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';
import { copyToClipboard } from '@speechki-frontend/shared/helpers/functions';

import SsmlEditor from '../SsmlEditor/SsmlEditorV3';

import NotesToolbar from '../NotesToolbar/NotesToolbar';

import { mapState, mapActions, mapGetters } from 'vuex';

//
import AudioEditor from '../AudioEditorV3/AudioEditor';
import EditorToolbarPlayer from './EditorToolbarPlayer';

import { fetchEmotions } from '../../api/emotions';

import { isRtlLanguage } from '@speechki-frontend/core/editor/utils';
export default {
    name: 'EditorToolbar',
    components: {
        NumberInput,
        CustomSelect,
        ErrorList,
        SsmlEditor,
        NotesToolbar,
        AudioEditor,
        Modal,
        SpeakerSelect,
        PaginatedResource,
        EditorToolbarPlayer,
    },
    inject: ['isEmbed'],
    props: {
        part: {
            type: [Object, Boolean],
            default: null,
            validator(value) {
                return value && value.id;
            },
        },

        readMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            saving: false,
            error: null,
            ssmlEditorActive: false,
            showWavePlayer: true,
            showAudioEditor: false,
        };
    },

    computed: {
        ...mapState({
            speakers: (state) => state.settings.speakers,
            languages: (state) => state.settings.languages,
            book: (state) => state.book.book,
            soundSettings: (state) => state.settings.soundSettings,
            user: (state) => state.auth.user,
        }),
        ...mapState('bookpart', {
            bookpart: (state) => state.bookpart,
            initialBookpart: (state) => state.initialBookpart,
            changed: (state) => state.changed,
            lang: (state) => state.language,
            loading: (state) => state.loading,
        }),
        ...mapGetters(['isAdmin', 'isReviewer']),

        bookpartSpeed: {
            get() {
                return this.bookpart.speed;
            },
            set(value) {
                this.updateSpeed(value);
            },
        },

        bookpartVolume: {
            get() {
                return this.bookpart.volume;
            },
            set(value) {
                this.updateVolume(value);
            },
        },

        bookpartSsmlText: {
            get() {
                return this.bookpart.volume;
            },
            set(value) {
                this.updateVolume(value);
            },
        },

        bookpartSoundSettings: {
            get() {
                return this.bookpart.sound_settings;
            },
            set(value) {
                this.updateSoundSettings(value);
            },
        },

        bookpartSpeaker: {
            get() {
                return typeof this.bookpart.speaker === 'object' && this.bookpart.speaker != null
                    ? this.bookpart.speaker.id
                    : this.bookpart.speaker;
            },
            set(value) {
                this.updateSpeaker(value);
            },
        },

        bookpartEmotion: {
            get() {
                return this.bookpart.emotion;
            },
            set(value) {
                this.updateEmotion(value);
            },
        },

        language: {
            get() {
                return this.lang;
            },
            set(value) {
                this.updateLanguage(value);
            },
        },

        speakerObject() {
            return this.speakers.find((sp) => sp.id === this.bookpartSpeaker) || {};
        },

        available() {
            return !!(this.bookpart.id && this.languages.length);
        },

        hasSpeaker() {
            return !!this.bookpart.speaker;
        },

        speakerSettings() {
            return this.speakerObject.extra_params || {};
        },

        filteredSoundSettings() {
            if (!this.bookpart || !this.bookpartSpeaker) return [];

            if (!this.bookpartSpeaker) return [];

            return this.soundSettings.filter((setting) => {
                let exists = setting.speakers.find((sp) => {
                    return sp.id === this.bookpartSpeaker;
                });
                return !!exists;
            });
        },

        hasChunkChanges() {
            const { audio_generation_extra } = this.bookpart;

            if (!audio_generation_extra) return;

            return !!(
                (audio_generation_extra.speed_chunks &&
                    audio_generation_extra.speed_chunks.length) ||
                (audio_generation_extra.trim_chunks && audio_generation_extra.trim_chunks.length)
            );
        },

        emotionsFilter() {
            return { speaker_slug: this.speakerObject.slug };
        },

        formattedError() {
            if (this.error) {
                return this.error;
            }

            if (this.bookpart.error_message) {
                return {
                    detail: this.bookpart.error_message,
                };
            }

            return null;
        },

        isRtl() {
            return isRtlLanguage(this.book.book_language);
        },
    },
    watch: {
        part: {
            handler() {
                this.loadData();
            },
            immediate: true,
        },

        'bookpart.id'() {
            this.saving = false;
        },

        async language(language, old) {
            if (!old) return;

            const { limit: limits } = this.book;
            await this.fetchSpeakers({ language, limits });
        },
    },

    async created() {
        if (!this.available) {
            await Promise.all([
                this.fetchLanguages({ limit: this.book.limit, page_size: 1000 }),
                this.fetchSoundSettings(),
            ]);
        }
    },

    methods: {
        ...mapActions(['fetchLanguages', 'fetchSpeakers', 'fetchSoundSettings']),
        ...mapActions('bookpart', [
            'updateSpeed',
            'updateVolume',
            'updateSsmlText',
            'updateSoundSettings',
            'updateEmotion',
            'updateSpeaker',
            'updateAudioGenerationExtra',
            'fetchBookpart',
            'resetBookpart',
            'updateLanguage',
            'saveAndGenerateBookpart',
        ]),

        fetchEmotions,

        async loadData() {
            this.ssmlEditorActive = false;
            this.error = null;

            if (!this.part || !this.part.id) {
                return;
            }

            await this.fetchBookpart(this.part.id);
            let limit = this.book.limit;
            await this.fetchSpeakers({
                limits: limit,
                language: this.language,
            });
        },

        async cancel() {
            this.resetBookpart();

            if (this.error) {
                await this.save();

                this.error = null;
                this.$emit('cancel');
                window.dispatchEvent(new CustomEvent('ssml-editor-cancel'));
                return;
            }

            // to listen on ssml (SsmlEditorV3 listens on window: $parent.$on is gone in Vue 3)
            this.$emit('cancel');
            window.dispatchEvent(new CustomEvent('ssml-editor-cancel'));
        },

        async save() {
            if (!this.bookpart.permissions.generate && this.isEmbed) {
                this.$store.dispatch('pushToast', {
                    type: 'error',
                    title: `Text can't be edited`,
                    text: `You’ve reached the editing limit in this project. <br> To read more about how editing limits in projects work, please follow <a class='link' href='https://speechki.org/faq' target='_blank'>this link</a>`,
                    theme: 'big',
                });
                return;
            }

            this.error = null;
            this.saving = true;

            try {
                await this.saveAndGenerateBookpart();

                this.$emit('save');
                window.dispatchEvent(new CustomEvent('ssml-editor-save'));
            } catch (e) {
                console.log(e);
                this.error = e;
            } finally {
                this.saving = false;
            }
        },

        onMassChange() {
            this.$emit('massChange');
            this.loadData();
        },

        copyLink() {
            copyToClipboard(location.href);
            this.$store.dispatch('pushToast', {
                id: Date.now(),
                type: 'success',
                title: this.$t('editor.toolbar.copyLinkSuccess'),
            });
        },

        copyText() {
            copyToClipboard(this.bookpart.raw_text);
            this.$store.dispatch('pushToast', {
                id: Date.now(),
                type: 'success',
                title: this.$t('editor.toolbar.copyTextSuccess'),
            });
        },

        async updateAudioEditorChunks(event) {
            this.updateAudioGenerationExtra(event);
            await this.save();
            this.showAudioEditor = false;
        },

        signalPlay() {
            this.$emit('play');
        },
    },
};
</script>

<style lang="scss" src="./EditorToolbar.scss"></style>
